import React, { useState } from 'react';
import styled from "styled-components";
import Dialog from "@shared/ui/molecules/Dialog";
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from '@shared/store/dialog.duck';
import { useTranslation } from "react-i18next";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { BodyLarge, BodyRegular, BodySmall, BodyVerySmall } from "@shared/ui/atoms/fonts/Body";
import {
  FreeBusyStatus,
  WhereaboutsOption,
  WhereaboutsOptionItem
} from '@features/whereabouts/services/WhereaboutOptions';
import Colours from "@shared/ui/atoms/Colours";
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { selectIconPack } from '@shared/store/auth.duck';
import { smallTablet } from "@shared/ui/atoms/MediaQueries";

const MAX_FREE_TEXT_LENGTH = 100;

export function WhereaboutsOptionPickerDialog(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const iconPack = useSelector(selectIconPack);
  const [freeText, setFreeText] = useState('');

  const whereabouts = props.payload.whereabouts;
  const optionItems = whereabouts.optionItems ?? [];

  const close = () => {
    dispatch(closeDialog());
  }

  const onItemSelected = (e: Event, item: WhereaboutsOptionItem) => {
    props.payload.onOptionPicked(e, item.id, undefined);
    close();
  }

  const onFreeTextSaved = (e: Event) => {
    props.payload.onOptionPicked(e, undefined, freeText.trim());
    close();
  }

  const onSkip = (e: Event) => {
    props.payload.onOptionPicked(e, undefined, undefined);
    close();
  }

  const onFreeTextChange = (val: string) => {
    setFreeText(val.substring(0, MAX_FREE_TEXT_LENGTH));
  }

  return (
    <DialogWrapper isOpen={true} onClose={close}>
      <Header>
        <WhereaboutsHeaderImage src={iconPackPath(iconPack, whereabouts.image, FreeBusyStatus.FREE)} alt={whereabouts.alt} />
        <BodyLarge weight={600}>{whereabouts.label}</BodyLarge>
      </Header>
      <Content>
        <BodySmall style={{marginBottom: 8}}>{t('whereabouts-option-picker-dialog.pick-option')}</BodySmall>
        <ItemList>
          {optionItems.map((item, index) => (
            <ItemButton key={item.id ?? index}
                        data-test={`whereabouts-option-item-${index}`}
                        onClick={(e: any) => onItemSelected(e, item)}>
              <BodyRegular weight={600}>{item.label}</BodyRegular>
            </ItemButton>
          ))}
        </ItemList>

        {whereabouts.optionListFreeTextEnabled && <>
          <BodySmall style={{marginTop: 16}}>{t('whereabouts-option-picker-dialog.or-free-text')}</BodySmall>
          <TextArea value={freeText}
                    data-test={'whereabouts-option-free-text'}
                    onChange={(e) => onFreeTextChange(e.target.value)} />
          <BodyVerySmall style={{textAlign: 'right'}} colour={Colours.darkGrey}>{MAX_FREE_TEXT_LENGTH - freeText.length} / {MAX_FREE_TEXT_LENGTH}</BodyVerySmall>
          <PrimaryButton style={{marginTop: 8}}
                         text={'button.save'}
                         dataTest={'save-whereabouts-option-free-text'}
                         click={onFreeTextSaved}
                         disabled={freeText.trim().length === 0}
                         fullWidth={true} />
        </>}

        <OutlineButton style={{margin: '16px auto 0 auto'}}
                       text={'whereabouts-option-picker-dialog.skip'}
                       dataTest={'skip-whereabouts-option'}
                       click={onSkip}
                       hideBorder={true}
                       fullWidth={true} />
      </Content>
    </DialogWrapper>
  )
}

interface Props {
  payload: {
    whereabouts: WhereaboutsOption;
    onOptionPicked: (e: Event, optionItemId?: number, freeText?: string) => any;
  };
}

const DialogWrapper = styled(Dialog)<any>`
  &.dialog {
    width: 100vw;
    height: 100vh;
    padding: 20px 0 0 0;
    @media (${smallTablet}) {
      padding: 20px 24px 24px 24px;
      max-width: 100%;
      min-height: 400px;
      width: unset;
      height: unset;
    }
  }
  .dialog__contentContainer, .dialog__content {
    width: 100%;
    padding-top: 0;
    @media (${smallTablet}) {
      width: 400px;
      min-height: 410px;
    }
  }
  .dialog__content {
    display: flex;
    flex-direction: column;
  }
`

const Header = styled.div`
  border-bottom: 1px solid ${Colours.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  min-height: 60px;
  margin-bottom: 24px;
`

const Content = styled.div`
  max-width: 100%;
`

const WhereaboutsHeaderImage = styled.img`
  position: absolute;
  left: 0;
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
`

const ItemButton = styled.div`
  cursor: pointer;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 8px;
  &:hover {
    background-color: ${Colours.blue10};
    border-color: ${Colours.blue};
  }
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 60px;
  margin: 8px 0 2px 0;
  padding: 4px 8px;
  font-size: 16px;
  font-family: "soleil", sans-serif;
  white-space: pre-wrap;
`
